import React, { useState } from 'react'
import {
  User,
  Building,
  Globe,
  Save,
  Check,
  AlertCircle,
  Lock,
  Mail,
  Phone,
  X,
  CreditCard,
  Eye,
  EyeOff,
  CheckCircle2,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import LoadingSpinner from '../common/LoadingSpinner'
import { nsConfirm } from '../shared/ns-confirm'

const ClientSettings = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState({
    company_name: '',
    domain: '',
    email: '',
    phone: '',
    address: '',
    description: ''
  })
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [show2FAModal, setShow2FAModal] = useState(false)

  // Stripe key state
  const [stripeKey, setStripeKey] = useState('')
  const [showStripeKey, setShowStripeKey] = useState(false)

  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { staleTime: 10 * 60 * 1000 }
  )

  // Fetch client profile
  const { data: profile, isLoading } = useQuery(
    'client-profile',
    () => apiService.get('/clients/profile').then(res => res.data),
    {
      onSuccess: (data) => {
        setFormData({
          company_name: data.company_name || '',
          domain: data.domain || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          description: data.description || ''
        })
      }
    }
  )

  // Fetch security information
  const { data: securityInfo, isLoading: securityLoading } = useQuery(
    'user-security',
    () => apiService.get('/auth/security').then(res => res.data),
    {
      staleTime: 5 * 60 * 1000
    }
  )

  // Update profile mutation
  const updateProfileMutation = useMutation(
    async (profileData) => {
      const response = await apiService.put('/clients/profile', profileData)

      await apiService.post('/clients/onboarding/update', {
        itemId: 'about',
        completed: true
      })

      if (profileData.domain && profileData.domain.trim()) {
        await apiService.post('/clients/onboarding/update', {
          itemId: 'domain',
          completed: true
        })
      }

      return response
    },
    {
      onSuccess: () => {
        toast.success('Settings saved successfully!')
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
        queryClient.invalidateQueries('user-security')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to save settings')
      }
    }
  )

  // Stripe status query
  const { data: stripeStatus, refetch: refetchStripe } = useQuery(
    'stripe-status',
    () => apiService.get('/clients/stripe').then(r => r.data),
    { staleTime: 60 * 1000 }
  )

  const saveStripeMutation = useMutation(
    (key) => apiService.put('/clients/stripe', { stripeSecretKey: key || null }),
    {
      onSuccess: () => {
        toast.success(stripeKey ? 'Stripe key saved' : 'Stripe key removed')
        setStripeKey('')
        refetchStripe()
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to save Stripe key'),
    }
  )

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    updateProfileMutation.mutate(formData)
  }

  if (isLoading) {
    return (
      <div className="card card-pad flex items-center justify-center" style={{ minHeight: '12rem' }}>
        <LoadingSpinner size="lg" text="Loading settings..." />
      </div>
    )
  }

  const [settingsTab, setSettingsTab] = useState('profile')
  const TABS = [['profile', 'Profile'], ['security', 'Security'], ['billing', 'Billing'], ['notifications', 'Notifications']]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Your account</div>
          <h1>Settings</h1>
          <div className="subtitle">Manage your profile, security and billing</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ alignSelf: 'flex-start' }}>
        {TABS.map(([id, label]) => (
          <button key={id} className={settingsTab === id ? 'active' : ''} onClick={() => setSettingsTab(id)}>{label}</button>
        ))}
      </div>

      {(settingsTab === 'billing' || settingsTab === 'notifications') && (
        <div className="card card-pad" style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
          <p style={{ fontSize: 13 }}>This section will be available soon.</p>
        </div>
      )}

      {/* Profile Information */}
      {settingsTab === 'profile' && <div className="card card-pad">
        <div className="flex items-center gap-2" style={{ marginBottom: '1.25rem' }}>
          <Building style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
          <h2 className="font-semibold" style={{ fontSize: '1rem', color: 'var(--text)', margin: 0 }}>Company Information</h2>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
            {/* Company Name */}
            <div>
              <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                Company Name *
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                required
                className="input w-full"
                placeholder="Enter your company name"
              />
            </div>

            {/* Email */}
            <div>
              <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                Email Address
              </label>
              <div style={{ position: 'relative' }}>
                <Mail style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', width: '1rem', height: '1rem', color: 'var(--text-faint)' }} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="input w-full"
                  style={{ paddingLeft: '2.25rem' }}
                  placeholder="company@example.com"
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                Phone Number
              </label>
              <div style={{ position: 'relative' }}>
                <Phone style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', width: '1rem', height: '1rem', color: 'var(--text-faint)' }} />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="input w-full"
                  style={{ paddingLeft: '2.25rem' }}
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>

            {/* Domain */}
            <div>
              <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                Website Domain
              </label>
              <div style={{ position: 'relative' }}>
                <Globe style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', width: '1rem', height: '1rem', color: 'var(--text-faint)' }} />
                <input
                  type="url"
                  name="domain"
                  value={formData.domain}
                  onChange={handleInputChange}
                  className="input w-full"
                  style={{ paddingLeft: '2.25rem' }}
                  placeholder="https://yourwebsite.com"
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
              Business Address
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              className="input w-full"
              placeholder="123 Business St, City, State 12345"
            />
          </div>

          {/* Description */}
          <div>
            <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
              Company Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className="input w-full"
              style={{ resize: 'vertical' }}
              placeholder="Tell us about your company..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={updateProfileMutation.isLoading}
            >
              {updateProfileMutation.isLoading ? (
                <>
                  <LoadingSpinner size="sm" />
                  Saving...
                </>
              ) : (
                <>
                  <Save style={{ width: '1rem', height: '1rem' }} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>}

      {settingsTab === 'security' && <>
      {/* Account Security */}
      <div className="card card-pad">
        <div className="flex items-center gap-2" style={{ marginBottom: '1.25rem' }}>
          <Lock style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
          <h2 className="font-semibold" style={{ fontSize: '1rem', color: 'var(--text)', margin: 0 }}>Account Security</h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div className="flex items-center justify-between" style={{ padding: '1rem', background: 'var(--surface-2)', borderRadius: 'var(--radius)' }}>
            <div>
              <h3 className="font-medium" style={{ color: 'var(--text)', margin: 0 }}>
                Password
              </h3>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                {securityLoading ? (
                  'Loading...'
                ) : securityInfo?.password_changed_at ? (
                  `Last updated ${new Date(securityInfo.password_changed_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}`
                ) : (
                  'Never changed'
                )}
              </p>
            </div>
            <button className="btn btn-ghost" onClick={() => setShowPasswordModal(true)}>
              Change Password
            </button>
          </div>

          <div className="flex items-center justify-between" style={{ padding: '1rem', background: 'var(--surface-2)', borderRadius: 'var(--radius)' }}>
            <div>
              <h3 className="font-medium" style={{ color: 'var(--text)', margin: 0 }}>
                Two-Factor Authentication
              </h3>
              <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                {securityInfo?.totp_enabled
                  ? 'Enabled — your account is protected with an authenticator app'
                  : 'Add an extra layer of security to your account'}
              </p>
            </div>
            <button
              className={securityInfo?.totp_enabled ? 'btn btn-danger' : 'btn btn-ghost'}
              onClick={() => setShow2FAModal(true)}
            >
              {securityInfo?.totp_enabled ? 'Disable 2FA' : 'Enable 2FA'}
            </button>
          </div>
        </div>
      </div>

      {/* Stripe Payments */}
      <div className="card card-pad">
        <div className="flex items-center gap-2" style={{ marginBottom: '1rem' }}>
          <CreditCard style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
          <h2 className="font-semibold" style={{ fontSize: '1rem', color: 'var(--text)', margin: 0 }}>Online Payments (Stripe)</h2>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Status badge */}
          <span className={stripeStatus?.configured ? 'badge badge-success' : 'badge'}>
            {stripeStatus?.configured
              ? <><CheckCircle2 style={{ width: '0.875rem', height: '0.875rem', display: 'inline', marginRight: '0.25rem' }} />Stripe connected</>
              : <><CreditCard style={{ width: '0.875rem', height: '0.875rem', display: 'inline', marginRight: '0.25rem' }} />Not configured</>}
          </span>

          <p className="text-muted" style={{ fontSize: '0.875rem' }}>
            Enter your Stripe secret key to accept card payments on your website.
            Your key is stored securely and never shown again after saving.{' '}
            <a
              href="https://dashboard.stripe.com/apikeys"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--accent)', textDecoration: 'underline' }}
            >
              Find your key →
            </a>
          </p>

          <div className="flex gap-2">
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                type={showStripeKey ? 'text' : 'password'}
                value={stripeKey}
                onChange={e => setStripeKey(e.target.value)}
                placeholder={stripeStatus?.configured ? '••••••••••••••••••• (leave blank to keep current)' : 'sk_live_... or sk_test_...'}
                className="input w-full"
                style={{ paddingRight: '2.5rem', fontFamily: 'monospace', fontSize: '0.875rem' }}
              />
              <button
                type="button"
                onClick={() => setShowStripeKey(v => !v)}
                style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)', padding: 0 }}
              >
                {showStripeKey ? <EyeOff style={{ width: '1rem', height: '1rem' }} /> : <Eye style={{ width: '1rem', height: '1rem' }} />}
              </button>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => saveStripeMutation.mutate(stripeKey)}
              disabled={saveStripeMutation.isLoading || (!stripeKey && !stripeStatus?.configured)}
            >
              {saveStripeMutation.isLoading ? 'Saving…' : 'Save'}
            </button>
            {stripeStatus?.configured && (
              <button
                className="btn btn-ghost"
                onClick={async () => {
                  const ok = await nsConfirm({
                    tone: 'danger', icon: 'trash',
                    title: 'Remove your Stripe key?',
                    message: 'Card payments will stop working on your sites until you add a new key.',
                    confirmLabel: 'Remove key',
                  })
                  if (ok) saveStripeMutation.mutate('')
                }}
                disabled={saveStripeMutation.isLoading}
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Subscription Info */}
      <div className="card card-pad">
        <div className="flex items-center gap-2" style={{ marginBottom: '1rem' }}>
          <User style={{ width: '1.25rem', height: '1.25rem', color: 'var(--accent)' }} />
          <h2 className="font-semibold" style={{ fontSize: '1rem', color: 'var(--text)', margin: 0 }}>Subscription</h2>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium" style={{ color: 'var(--text)', margin: 0 }}>
              Current Plan: {plansRaw?.[profile?.subscription_plan]?.name || profile?.subscription_plan?.toUpperCase() || 'BASIC'}
            </h3>
            <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
              {profile?.edit_quota || 50} edits per month
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => navigate('/client/billing')}>
            Upgrade Plan
          </button>
        </div>
      </div>
      </>}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <PasswordChangeModal
          onClose={() => setShowPasswordModal(false)}
          queryClient={queryClient}
        />
      )}

      {/* 2FA Modal */}
      {show2FAModal && (
        <TwoFactorModal
          isEnabled={!!securityInfo?.totp_enabled}
          onClose={() => { setShow2FAModal(false); queryClient.invalidateQueries('user-security') }}
        />
      )}
    </div>
  )
}

// Password Change Modal Component
const PasswordChangeModal = ({ onClose, queryClient }) => {
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (passwords.newPassword !== passwords.confirmPassword) {
      setError('New passwords do not match')
      return
    }

    if (passwords.newPassword.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    setIsLoading(true)
    try {
      await apiService.put('/auth/password', {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword
      })
      setSuccess(true)
      toast.success('Password changed successfully!')
      queryClient.invalidateQueries('user-security')
      setTimeout(() => {
        onClose()
      }, 2000)
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to change password'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '32rem' }}>
        <form onSubmit={handleSubmit}>
          <div className="modal-head">
            <h2 className="font-semibold" style={{ fontSize: '1.125rem', color: 'var(--text)', margin: 0 }}>
              Change Password
            </h2>
            <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
              <X style={{ width: '1.25rem', height: '1.25rem' }} />
            </button>
          </div>
          <div className="modal-body">
            {success ? (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <Check style={{ width: '3rem', height: '3rem', color: 'var(--success)', margin: '0 auto 1rem' }} />
                <p style={{ color: 'var(--success)' }}>Password changed successfully!</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={passwords.currentPassword}
                    onChange={(e) => setPasswords(prev => ({ ...prev, currentPassword: e.target.value }))}
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwords.newPassword}
                    onChange={(e) => setPasswords(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="input w-full"
                    required
                    minLength={6}
                  />
                </div>
                <div>
                  <label className="font-medium" style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={passwords.confirmPassword}
                    onChange={(e) => setPasswords(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="input w-full"
                    required
                    minLength={6}
                  />
                </div>

                {error && (
                  <div style={{ padding: '0.75rem', background: 'var(--danger-soft)', border: '1px solid var(--danger)', borderRadius: 'var(--radius-sm)' }}>
                    <div className="flex items-center gap-2">
                      <AlertCircle style={{ width: '1rem', height: '1rem', color: 'var(--danger)', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.875rem', color: 'var(--danger)' }}>{error}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {!success && (
            <div className="modal-foot">
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Changing...
                  </>
                ) : (
                  'Change Password'
                )}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

// Two Factor Authentication Modal Component
const TwoFactorModal = ({ isEnabled, onClose }) => {
  const [step, setStep] = useState(isEnabled ? 'disable' : 'info')
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSetup = async () => {
    setIsLoading(true)
    setError('')
    try {
      const response = await apiService.post('/auth/2fa/setup')
      setQrCode(response.data.qrCode)
      setSecret(response.data.secret)
      setStep('setup')
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to setup 2FA')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerify = async (e) => {
    e?.preventDefault()
    setIsLoading(true)
    setError('')
    try {
      await apiService.post('/auth/2fa/verify', { token: code })
      setStep('success')
      toast.success('2FA enabled successfully!')
      setTimeout(onClose, 2000)
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid verification code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDisable = async (e) => {
    e?.preventDefault()
    setIsLoading(true)
    setError('')
    try {
      await apiService.post('/auth/2fa/disable', { token: code })
      setStep('disabled')
      toast.success('2FA disabled')
      setTimeout(onClose, 2000)
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid authenticator code')
    } finally {
      setIsLoading(false)
    }
  }

  const ErrorBox = ({ msg }) => msg ? (
    <div style={{ padding: '0.75rem', background: 'var(--danger-soft)', border: '1px solid var(--danger)', borderRadius: 'var(--radius-sm)' }}>
      <div className="flex items-center gap-2">
        <AlertCircle style={{ width: '1rem', height: '1rem', color: 'var(--danger)', flexShrink: 0 }} />
        <span style={{ fontSize: '0.875rem', color: 'var(--danger)' }}>{msg}</span>
      </div>
    </div>
  ) : null

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '32rem' }}>
        <div className="modal-head">
          <h2 className="font-semibold" style={{ fontSize: '1.125rem', color: 'var(--text)', margin: 0 }}>
            Two-Factor Authentication
          </h2>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
            <X style={{ width: '1.25rem', height: '1.25rem' }} />
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Info step */}
          {step === 'info' && (
            <>
              <p className="text-soft">
                Adds an extra layer of security — you'll need a code from your authenticator app each time you log in.
              </p>
              <ul style={{ listStyle: 'disc', paddingLeft: '1.25rem', fontSize: '0.875rem', color: 'var(--text-soft)', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                <li>Google Authenticator</li>
                <li>Authy</li>
                <li>Any TOTP-compatible app</li>
              </ul>
              <ErrorBox msg={error} />
            </>
          )}

          {/* Setup step */}
          {step === 'setup' && (
            <>
              <p className="text-soft" style={{ fontSize: '0.875rem' }}>
                Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.
              </p>
              {qrCode && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem', background: 'var(--surface)', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                  <img src={qrCode} alt="2FA QR Code" style={{ width: '12rem', height: '12rem' }} />
                </div>
              )}
              <div>
                <p className="text-faint" style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }}>Can't scan? Enter this key manually:</p>
                <code style={{ display: 'block', padding: '0.5rem', background: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', fontSize: '0.75rem', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                  {secret}
                </code>
              </div>
              <form onSubmit={handleVerify} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input w-full"
                  style={{ textAlign: 'center', fontSize: '1.25rem', letterSpacing: '0.2em' }}
                  placeholder="000000"
                  autoFocus
                  required
                />
                <ErrorBox msg={error} />
              </form>
            </>
          )}

          {/* Disable step */}
          {step === 'disable' && (
            <>
              <p className="text-soft">
                Enter the current 6-digit code from your authenticator app to disable 2FA.
              </p>
              <form onSubmit={handleDisable} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input w-full"
                  style={{ textAlign: 'center', fontSize: '1.25rem', letterSpacing: '0.2em' }}
                  placeholder="000000"
                  autoFocus
                  required
                />
                <ErrorBox msg={error} />
              </form>
            </>
          )}

          {/* Success / disabled */}
          {(step === 'success' || step === 'disabled') && (
            <div style={{ textAlign: 'center', padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
              <Check style={{ width: '3rem', height: '3rem', color: 'var(--success)' }} />
              <p className="font-medium" style={{ color: 'var(--text)' }}>
                {step === 'success' ? '2FA enabled successfully!' : '2FA has been disabled'}
              </p>
            </div>
          )}

        </div>

        {!['success', 'disabled'].includes(step) && (
          <div className="modal-foot">
            {step === 'info' && (
              <>
                <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSetup} disabled={isLoading}>
                  {isLoading ? <><LoadingSpinner size="sm" />Loading…</> : 'Get Started'}
                </button>
              </>
            )}
            {step === 'setup' && (
              <>
                <button className="btn btn-ghost" onClick={() => setStep('info')}>Back</button>
                <button className="btn btn-primary" onClick={handleVerify} disabled={isLoading || code.length !== 6}>
                  {isLoading ? <><LoadingSpinner size="sm" />Verifying…</> : 'Verify & Enable'}
                </button>
              </>
            )}
            {step === 'disable' && (
              <>
                <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
                <button className="btn btn-danger" onClick={handleDisable} disabled={isLoading || code.length !== 6}>
                  {isLoading ? <><LoadingSpinner size="sm" />Disabling…</> : 'Disable 2FA'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ClientSettings
